/**
 * Prints the content of a given HTML element in a new tab and closes the tab after printing or canceling.
 * @param {React.RefObject} contentRef - The ref to the HTML element to be printed.
 * @param {string} title - Optional title for the print window.
 * @param {string} printStyle - Optional custom CSS styles to override table formatting.
 */
export const printContent = (contentRef, title = "Print", styleTable = true) => {
    if (!contentRef.current) {
        console.error("Invalid content reference.");
        return;
    }

    const printContent = contentRef.current.innerHTML;

    // Default table style
    const tableStyle = `
        body { font-family: Arial, sans-serif; margin: 20px; }
        .text-right { text-align: right; }
        .summary-table { width: 50%; float: right; margin-left: 20px; }
        .content-wrapper { width: 100%; overflow: hidden; }
         table { width: 100%; border-collapse: collapse; }
        table, th, td { border: 1px solid #ccc; }
        th { background-color: #f5f5f5; }
        th, td { padding: 8px; text-align: left; }
    `;

     const noTableStyle = `
        body { font-family: Arial, sans-serif; margin: 20px; }
        .text-right { text-align: right; }
        .summary-table { width: 50%; float: right; margin-left: 20px; }
        .content-wrapper { width: 100%; overflow: hidden; }
    `;

    const finalStyle = styleTable ? tableStyle : noTableStyle;

    // Open a new tab
    const printWindow = window.open("", "_blank");

    if (!printWindow) {
        console.error("Failed to open a new window/tab.");
        return;
    }

    printWindow.document.open();
    printWindow.document.write(`
        <html>
            <head>
                <title>${title}</title>
                <style>${finalStyle}</style>
            </head>
            <body>
                ${printContent}
            </body>
        </html>
    `);
    printWindow.document.close();

    printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();

        const closeCheck = setInterval(() => {
            if (printWindow.closed) {
                clearInterval(closeCheck);
            } else if (!printWindow.document.hasFocus()) {
                clearInterval(closeCheck);
                printWindow.close();
            }
        }, 500);
    };
};

 export const formatMoney = (amount, currency = 'Ush') =>
  new Intl.NumberFormat('en-UG', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(amount) || 0);

export const humanMinutes = (mins) => {
  if (!mins) return null;
  const n = Number(mins);
  if (!Number.isFinite(n) || n <= 0) return `${mins} minute(s)`;
  if (n % 1440 === 0) return `${n / 1440} day(s)`;
  if (n % 60 === 0) return `${n / 60} hour(s)`;
  return `${n} minute(s)`;
};

export const humanDataLimit = (mb) => {
  if (!mb) return null;
  const n = Number(mb);
  if (!Number.isFinite(n)) return `${mb} MB`;
  return n >= 1024 ? `${(n / 1024).toFixed(n % 1024 === 0 ? 0 : 1)} GB` : `${n} MB`;
};

export const yesNo = (v) => (String(v) === '1' || v === true ? 'Yes' : 'No');
export const pubHidden = (v) => (String(v) === '1' || v === true ? 'Public' : 'Hidden');

export const SpeedsLine = ({ plan }) => {
  const d = plan.download_mbps;
  const u = plan.upload_mbps;
  const rt = plan.mikrotik_rate_limit; // e.g. "2M/1M" or "2M/1M 5M/3M 10/10 100 50"
  if (d || u) {
    return (
      <li>
        <b>Speeds:</b> {d ? `${d} Mbps ↓` : '—'}{u ? ` / ${u} Mbps ↑` : ''}
      </li>
    );
  }
  if (rt) return <li><b>MikroTik Rate Limit:</b> {rt}</li>;
  return null;
};

export const numberFormat = (number, { minDigits = 2 } = {}) => {
        // Normalize input
        const n = Number(number);
        if (!isFinite(n)) return "00";

        const sign = n < 0 ? "-" : "";
        let [intPart, fracPart] = Math.abs(n).toString().split(".");

        // Pad with leading zeros if requested (e.g. 2 -> "02")
        if (minDigits > 0) {
            intPart = intPart.padStart(minDigits, "0");
        }

        // Add thousands separators to the integer part
        intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

        return sign + (fracPart ? `${intPart}.${fracPart}` : intPart);
    };

    export const formatDate = (dateString) => {
        // Parse the input date string
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    
        // Check if the input contains time (datetime format)
        const hasTime = dateString.includes("T") || dateString.includes(" ");
    
        if (hasTime) {
            // Handle datetime format
            const [datePart, timePart] = dateString.split(/[T ]/); // Split by "T" or space
            const [year, month, day] = datePart.split("-");
            let [hours, minutes] = timePart.split(":");
    
            // Convert hours to 12-hour format and determine AM/PM
            const isPM = parseInt(hours, 10) >= 12;
            hours = (parseInt(hours, 10) % 12) || 12; // Convert 0 or 24-hour to 12-hour format
            const formattedTime = `${hours}:${minutes}${isPM ? "pm" : "am"}`;
    
            // Format the date with time
            return `${day}-${months[parseInt(month, 10) - 1]}-${year} ${formattedTime}`;
        } else {
            // Handle date-only format
            const [year, month, day] = dateString.split("-");
            return `${day}-${months[parseInt(month, 10) - 1]}-${year}`;
        }
    };

    export const tell_when = (datetime) => {
        if (!datetime) return '';

        // Normalize input (supports "YYYY-MM-DD HH:mm" and ISO)
        const d = new Date(datetime.replace(' ', 'T'));
        if (isNaN(d.getTime())) return datetime;

        const now = new Date();

        const isSameDay = (a, b) =>
            a.getFullYear() === b.getFullYear() &&
            a.getMonth() === b.getMonth() &&
            a.getDate() === b.getDate();

        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);

        const tomorrow = new Date(now);
        tomorrow.setDate(now.getDate() + 1);

        // Format time → 1:00pm
        let hours = d.getHours();
        const minutes = d.getMinutes().toString().padStart(2, '0');
        const isPM = hours >= 12;
        hours = (hours % 12) || 12;
        const time = `${hours}:${minutes}${isPM ? 'pm' : 'am'}`;

        if (isSameDay(d, now)) {
            return `Today, ${time}`;
        }

        if (isSameDay(d, yesterday)) {
            return `Yesterday, ${time}`;
        }

        if (isSameDay(d, tomorrow)) {
            return `Tomorrow, ${time}`;
        }

        // Fallback → 02 Jan 2026, 1:00pm
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}, ${time}`;
    };
